import React, { useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { BarChart2 } from "lucide-react";
import "./chartSetup";
import DateRangeFilter, {
  type DateRangeValue,
  getPresetDates,
} from "./DateRangeFilter";
import { ChartSkeleton } from "../Common/Skeleton";
import WidgetErrorState from "../Common/WidgetErrorState";
import EmptyState from "../Common/EmptyState";
import StaleDataBanner from "../Common/StaleDataBanner";

export interface LineChartPoint {
  value: number;
  label?: string;
}

export interface OnboardingLineChartProps {
  title: string;
  icon?: React.ReactNode;
  points?: LineChartPoint[];
  dates?: (string | Date | null | undefined)[];
  color?: string;
  granularity?: "month" | "week" | "day";
  onGranularityChange?: (g: "month" | "week" | "day") => void;
  dateRange?: DateRangeValue;
  onDateRangeChange?: (range: DateRangeValue) => void;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  isStale?: boolean;
  onResetDateRange?: () => void;
  emptyActionTo?: string;
  emptyActionLabel?: string;
  disabled?: boolean;
}

/**
 * Dynamically computes trend series points from timestamps and an active date window.
 * - <= 31 days: daily buckets
 * - <= 90 days: weekly buckets
 * - > 90 days: monthly buckets
 */
export function computeTrendPoints(
  dates: (string | Date | null | undefined)[],
  startDate: Date,
  endDate: Date
): { value: number; label: string }[] {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999);

  const diffMs = Math.max(0, end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  const validDates: Date[] = [];
  for (const raw of dates) {
    if (!raw) continue;
    const d = new Date(raw);
    if (isNaN(d.getTime())) continue;
    if (d >= start && d <= end) {
      validDates.push(d);
    }
  }

  // 1. Daily buckets (diff <= 31 days)
  if (diffDays <= 31) {
    const buckets: Record<string, number> = {};
    for (const d of validDates) {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      buckets[key] = (buckets[key] ?? 0) + 1;
    }

    const out: { value: number; label: string }[] = [];
    const cur = new Date(start);
    while (cur <= end) {
      const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
      const label = `${String(cur.getMonth() + 1).padStart(2, "0")}/${String(cur.getDate()).padStart(2, "0")}`;
      out.push({ value: buckets[key] ?? 0, label });
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }

  // 2. Weekly buckets (diff <= 90 days)
  if (diffDays <= 90) {
    const out: { value: number; label: string }[] = [];
    const cur = new Date(start);
    while (cur <= end) {
      const nextWeek = new Date(cur);
      nextWeek.setDate(nextWeek.getDate() + 7);
      const count = validDates.filter((d) => d >= cur && d < nextWeek).length;
      const label = cur.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      out.push({ value: count, label });
      cur.setDate(cur.getDate() + 7);
    }
    return out;
  }

  // 3. Monthly buckets (diff > 90 days)
  const buckets: Record<string, number> = {};
  for (const d of validDates) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    buckets[key] = (buckets[key] ?? 0) + 1;
  }

  const out: { value: number; label: string }[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

  while (cur <= endMonth) {
    const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`;
    const monthShort = cur.toLocaleDateString("en-US", { month: "short" });
    const label = `${monthShort} '${String(cur.getFullYear()).slice(2)}`;
    out.push({ value: buckets[key] ?? 0, label });
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
}

const OnboardingLineChart: React.FC<OnboardingLineChartProps> = ({
  title,
  icon,
  points,
  dates,
  color = "#15BBAE",
  granularity,
  onGranularityChange,
  dateRange: controlledDateRange,
  onDateRangeChange,
  loading = false,
  error = null,
  onRetry,
  isStale = false,
  onResetDateRange,
  emptyActionTo,
  emptyActionLabel,
  disabled = false,
}) => {
  // Default range: Last 6 Months (as in screenshot)
  const [internalDateRange, setInternalDateRange] = useState<DateRangeValue>(() => {
    const { startDate, endDate } = getPresetDates("last6m");
    return { startDate, endDate, preset: "last6m" };
  });

  const activeRange = controlledDateRange ?? internalDateRange;

  const handleRangeChange = (nextRange: DateRangeValue) => {
    if (!controlledDateRange) {
      setInternalDateRange(nextRange);
    }
    onDateRangeChange?.(nextRange);

    // Backward compatibility with granularity callers
    if (onGranularityChange) {
      const diffDays = Math.ceil(
        (nextRange.endDate.getTime() - nextRange.startDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (diffDays <= 31) {
        onGranularityChange("day");
      } else if (diffDays <= 90) {
        onGranularityChange("week");
      } else {
        onGranularityChange("month");
      }
    }
  };

  // Derive points: prefer computing from `dates` array if provided; otherwise fallback to `points`
  const chartPoints = useMemo(() => {
    if (dates && Array.isArray(dates) && dates.length > 0) {
      return computeTrendPoints(dates, activeRange.startDate, activeRange.endDate);
    }
    return points ?? [];
  }, [dates, points, activeRange]);

  const isAllZero = chartPoints.length === 0 || chartPoints.every((p) => p.value === 0);

  const data = useMemo(
    () => ({
      labels: chartPoints.map((p) => p.label ?? ""),
      datasets: [
        {
          data: chartPoints.map((p) => p.value),
          borderColor: color,
          backgroundColor: color,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 3,
          pointBackgroundColor: color,
          fill: false,
        },
      ],
    }),
    [chartPoints, color]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#16181D",
          padding: 10,
          titleColor: "#fff",
          bodyColor: "rgba(255,255,255,0.85)",
          displayColors: false,
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: "#8A929B" } },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: "#8A929B" },
          grid: { color: "#E4EAF0" },
        },
      },
    }),
    []
  );

  // Initial loading state with no prior data
  if (loading && isAllZero && !error && !isStale) {
    return <ChartSkeleton height={220} />;
  }

  // Hard failure with no prior data
  if (error && chartPoints.length === 0) {
    return (
      <div className="space-y-3 rounded-lg border border-[#E4EAF0] bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="flex items-center gap-1.5 text-[15px] font-semibold text-[#16181D]">
            <span aria-hidden="true" className="flex items-center text-primary-500">
              {icon}
            </span>
            {title}
          </h3>
        </div>
        <WidgetErrorState
          title={`${title} Unavailable`}
          message={error}
          onRetry={onRetry}
          variant="card"
          className="border-0 shadow-none py-6"
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ── Card Header: Title on Left, Date Range Filter on Right ── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0 text-primary-500">
          {icon}
          <h3 className="text-[15px] font-semibold text-[#16181D] truncate">{title}</h3>
        </div>

        {/* Date Range Picker */}
        <DateRangeFilter
          value={activeRange}
          onChange={handleRangeChange}
        />
      </div>

      {/* Stale data indication when refresh failed */}
      {isStale && (
        <StaleDataBanner
          message="Failed to update chart for the selected range. Showing previous trend data."
          onRetry={onRetry}
        />
      )}

      {/* Empty State vs Chart Canvas */}
      {isAllZero && !loading ? (
        <div className="relative h-[220px] w-full min-w-0 flex items-center justify-center">
          <EmptyState
            icon={<BarChart2 size={24} />}
            title="No onboarding activity in this period"
            description="No registrations were recorded for the selected date range. Try broadening the date range."
            action={
              onResetDateRange
                ? {
                    label: "Reset Date Range",
                    onClick: onResetDateRange,
                  }
                : emptyActionTo && emptyActionLabel
                ? {
                    label: emptyActionLabel,
                    to: emptyActionTo,
                  }
                : undefined
            }
            secondaryAction={
              onResetDateRange && emptyActionTo && emptyActionLabel
                ? {
                    label: emptyActionLabel,
                    to: emptyActionTo,
                  }
                : undefined
            }
            variant="chart"
          />
        </div>
      ) : (
        <div className="relative h-[220px] w-full min-w-0">
          <Line data={data} options={options} />
          {loading && (
            <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/40 backdrop-blur-[1px] transition-all">
              <div className="mt-3 flex items-center gap-2 rounded-full border border-[#E4EAF0] bg-white/95 px-3 py-1 text-xs font-semibold text-[#63788E] shadow-sm">
                <div className="h-3 w-3 animate-spin rounded-full border border-primary-600 border-t-transparent" />
                Updating...
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OnboardingLineChart;
